import React, { useState, useEffect, useRef } from "react";
import {
  Share2, // NEW: Icon cho copy
  Check, // NEW: Icon cho success feedback
} from "lucide-react";
import { useCallback } from "react";
import {
  ChevronLeft,
  Camera,
  Upload,
  MapPin,
  Phone,
  Clock,
  Edit2,
  Save,
  X,
  Copy,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Navigation,
  Plus,
  Minus,
  User,
  Truck,
  Calendar as CalendarIcon,
  Trash2,
} from "lucide-react";
import {
  shipmentService,
  masterDataService,
  imageService,
} from "../services/apiService";
import {
  formatShipmentInfoForCustomer,
  formatCompleteShipmentInfo,
  copyToClipboard,
} from "../utils/shipmentCopyUtils";
import ImageUpload from "./ImageUpload";

import {
  SHIPMENT_STATUS,
  SHIPMENT_STATUS_TEXT,
  SHIPMENT_STATUS_COLORS,
  SHIPMENT_DETAIL_FIELDS,
  CUSTOMER_FIELDS,
  DRIVER_FIELDS,
  TRUCK_FIELDS,
  PAYLOAD_FIELDS,
  TRANSPORT_TYPES,
  USER_TYPES,
  SHIPPING_ADDRESS_FIELDS,
} from "../constants";

const ShipmentDetail = ({ shipmentId, onBack, userData }) => {
  const [shipmentDetail, setShipmentDetail] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [payloads, setPayloads] = useState([]);
  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState("");
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedShipment, setEditedShipment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [deletingImageId, setDeletingImageId] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [uploadedImagesInModal, setUploadedImagesInModal] = useState([]);
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyingType, setCopyingType] = useState(null);

  // Refs for sections
  const imagesSectionRef = useRef(null);
  const imageUploadSectionRef = useRef(null);

  // New state for section collapsing
  const [collapsedSections, setCollapsedSections] = useState({
    basicInfo: userData?.type === USER_TYPES.DRIVER, // Collapsed by default for drivers
    vehicleInfo: userData?.type === USER_TYPES.DRIVER,
    notes: userData?.type === USER_TYPES.DRIVER,
    images: true, // Images section collapsed by default
    imageUpload: true, // Image upload section collapsed by default
  });

  const formatDateFromAPIToInput = (apiDate) => {
    if (!apiDate) return "";
    // API format: DD-MM-YYYY -> Input format: YYYY-MM-DD
    const parts = apiDate.split("-");
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return "";
  };

  const formatDateFromInputToAPI = (inputDate) => {
    if (!inputDate) return "";
    // Input format: YYYY-MM-DD -> API format: DD-MM-YYYY
    const parts = inputDate.split("-");
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return "";
  };

  const handleImageUploadComplete = (result) => {
    console.log("Image upload completed:", result);

    if (result.successful > 0) {
      alert(`Upload thành công ${result.successful} ảnh!`);

      // Reload images after successful upload
      loadShipmentImages();
    }
  };

  const handleCopyDriverInfo = async () => {
  if (!shipmentDetail || !driver || !truck) {
    alert('Không đủ thông tin để sao chép');
    return;
  }

  setCopyingType('driver');
  
  try {
    const formattedText = formatShipmentInfoForCustomer(shipmentDetail, driver, truck);
    const success = await copyToClipboard(formattedText);
    console.log("handleCopyDriverInfo :", success);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(false);
        setCopyingType(null);
      }, 2000);
    } else {
      alert('Không thể sao chép. Vui lòng thử lại.');
      setCopyingType(null);
    }
  } catch (error) {
    console.error('Copy error:', error);
    alert('Có lỗi xảy ra khi sao chép');
    setCopyingType(null);
  }
};

// Function để handle copy thông tin đầy đủ
const handleCopyCompleteInfo = async () => {
  if (!shipmentDetail || !driver || !truck) {
    alert('Không đủ thông tin để sao chép');
    return;
  }

  setCopyingType('complete');
  
  try {
    const formattedText = formatCompleteShipmentInfo(shipmentDetail, driver, truck);
    const success = await copyToClipboard(formattedText);
    console.log("handleCopyCompleteInfo :", success);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(false);
        setCopyingType(null);
      }, 2000);
    } else {
      alert('Không thể sao chép. Vui lòng thử lại.');
      setCopyingType(null);
    }
  } catch (error) {
    console.error('Copy error:', error);
    alert('Có lỗi xảy ra khi sao chép');
    setCopyingType(null);
  }
};

  // Handle image upload completion in modal
  const handleModalImageUploadComplete = (result) => {
    console.log("Modal image upload completed:", result);
    if (result.successful > 0) {
      setUploadedImagesInModal((prev) => [...prev, ...result.images]);
      console.log(
        "Uploaded images in modal:",
        result.images,
        uploadedImagesInModal
      );
    }
  };

  // Handle status update after image upload
  const completeStatusUpdateAfterUpload = async () => {
    if (!pendingStatusChange) return;

    setShowUploadModal(false);

    // Proceed with status update
    const newStatus = pendingStatusChange;
    setPendingStatusChange(null);
    setUploadedImagesInModal([]);

    setUpdating(true);
    try {
      // Pass userName from userData to the API call
      const result = await shipmentService.updateStatus(
        shipmentId,
        newStatus,
        userData?.UserName || userData?.userName || ""
      );

      if (result.success) {
        setShipmentDetail((prev) => ({
          ...prev,
          [SHIPMENT_DETAIL_FIELDS.STATUS]: newStatus,
        }));
        alert("Cập nhật trạng thái thành công");

        // Reload images to show newly uploaded ones
        loadShipmentImages();
      } else {
        alert(result.message || "Cập nhật trạng thái thất bại");
      }
    } catch (error) {
      alert("Có lỗi xảy ra khi cập nhật trạng thái");
    } finally {
      setUpdating(false);
    }
  };

  const handleImageUploadError = (error) => {
    console.error("Image upload error:", error);
    alert(`Lỗi upload ảnh: ${error}`);
  };

  const formatTimeFromHourMinute = (hour, minute) => {
    if (!hour && !minute) return "";
    const h = hour ? String(hour).padStart(2, "0") : "00";
    const m = minute ? String(minute).padStart(2, "0") : "00";
    return `${h}:${m}`;
  };

  const parseTimeToHourMinute = (timeString) => {
    if (!timeString) return { hour: "", minute: "" };
    const [hour, minute] = timeString.split(":");
    return {
      hour: hour ? hour : "",
      minute: minute ? minute : "",
    };
  };

  // Function to scroll to images section and expand it
  const scrollToImagesSection = useCallback(() => {
    if (imagesSectionRef.current) {
      // Expand the images section first
      setCollapsedSections((prev) => ({
        ...prev,
        images: false,
      }));

      // Then scroll to it with a small delay to allow expansion
      setTimeout(() => {
        imagesSectionRef.current.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, []);

  // Swipe functionality
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    loadShipmentDetail();
    loadMasterData();
    loadShipmentImages();
  }, [shipmentId]);

  // Intersection Observer to auto-expand sections when scrolled into view
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1, // Trigger when 10% of the section is visible
      rootMargin: "0px 0px -50px 0px", // Trigger slightly before the section is fully visible
    };

    // Observer callback function
    const observerCallback = (sectionName) => (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && collapsedSections[sectionName]) {
          // Auto-expand when section comes into view
          setCollapsedSections((prev) => ({
            ...prev,
            [sectionName]: false,
          }));
        }
      });
    };

    // Create observers for each section
    const imagesObserver = new IntersectionObserver(
      observerCallback("images"),
      observerOptions
    );

    const imageUploadObserver = new IntersectionObserver(
      observerCallback("imageUpload"),
      observerOptions
    );

    // Observe sections if they exist
    if (imagesSectionRef.current) {
      imagesObserver.observe(imagesSectionRef.current);
    }

    if (imageUploadSectionRef.current) {
      imageUploadObserver.observe(imageUploadSectionRef.current);
    }

    // Cleanup
    return () => {
      if (imagesSectionRef.current) {
        imagesObserver.unobserve(imagesSectionRef.current);
      }
      if (imageUploadSectionRef.current) {
        imageUploadObserver.unobserve(imageUploadSectionRef.current);
      }
    };
  }, [collapsedSections, images.length]);

  // Load shipment images
  const loadShipmentImages = async () => {
    try {
      const result = await imageService.getShipmentImages(shipmentId);
      if (result.success && result.data) {
        setImages(result.data);
      }
    } catch (err) {
      console.error("Error loading shipment images:", err);
    }
  };

  // Delete image from server
  const handleDeleteImage = async (imageId, filePath) => {
    if (!filePath) {
      alert("Không thể xóa ảnh: Đường dẫn không hợp lệ");
      return;
    }

    const confirmDelete = window.confirm(
      "Bạn có chắc chắn muốn xóa ảnh này không?"
    );
    if (!confirmDelete) return;

    setDeletingImageId(imageId);

    try {
      const result = await imageService.deleteImage(filePath);

      if (result.success) {
        // Remove from local state
        setImages((prev) => prev.filter((img) => img.id !== imageId));
        alert("Xóa ảnh thành công");

        // Reload images to ensure we have the latest data
        loadShipmentImages();
      } else {
        alert(result.message || "Không thể xóa ảnh");
      }
    } catch (error) {
      alert(`Lỗi khi xóa ảnh: ${error.message}`);
    } finally {
      setDeletingImageId(null);
    }
  };

  // Swipe functionality
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    if (!touchStartX.current || !touchStartY.current) return;

    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const deltaX = touchEndX - touchStartX.current;
    const deltaY = touchEndY - touchStartY.current;

    // Check if it's a horizontal swipe (more horizontal than vertical)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      // Swipe right to go back
      if (deltaX > 0) {
        onBack();
      }
    }

    touchStartX.current = null;
    touchStartY.current = null;
  };

  // Phone number handling
  const extractPhoneNumbers = (contactText) => {
    if (!contactText) return [];

    // Regular expression to match Vietnamese phone numbers
    const phoneRegex =
      /(?:\+84|84|0)(?:3[2-9]|5[689]|7[06-9]|8[1-689]|9[0-46-9])\d{7}/g;
    const matches = contactText.match(phoneRegex) || [];

    // Also look for any sequence of 10-11 digits
    const digitRegex = /\b\d{10,11}\b/g;
    const digitMatches = contactText.match(digitRegex) || [];

    // Combine and deduplicate
    const allNumbers = [...new Set([...matches, ...digitMatches])];
    return allNumbers;
  };

  const makePhoneCall = (phoneNumber) => {
    // Clean the phone number
    const cleanNumber = phoneNumber.replace(/\D/g, "");
    window.location.href = `tel:${cleanNumber}`;
  };

  const renderContactWithPhones = (contactText) => {
    if (!contactText) return "N/A";

    const phoneNumbers = extractPhoneNumbers(contactText);

    if (phoneNumbers.length === 0) {
      return <span className="font-medium">{contactText}</span>;
    }

    if (phoneNumbers.length === 1) {
      return (
        <div className="flex items-center gap-2">
          <span className="font-medium flex-1">{contactText}</span>
          <button
            onClick={() => makePhoneCall(phoneNumbers[0])}
            className="bg-green-500 text-white px-3 py-1 rounded-lg hover:bg-green-600 flex items-center gap-1 text-sm"
            title="Gọi điện"
          >
            <Phone className="w-4 h-4" />
            Gọi
          </button>
        </div>
      );
    }

    // Multiple phone numbers
    return (
      <div className="space-y-2">
        <span className="font-medium block">{contactText}</span>
        <div className="flex flex-wrap gap-2">
          {phoneNumbers.map((phone, index) => (
            <button
              key={index}
              onClick={() => makePhoneCall(phone)}
              className="bg-green-500 text-white px-3 py-1 rounded-lg hover:bg-green-600 flex items-center gap-1 text-sm"
              title={`Gọi ${phone}`}
            >
              <Phone className="w-4 h-4" />
              {phone}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const toggleSection = (section) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // const copyToClipboard = async (text) => {
  //   try {
  //     await navigator.clipboard.writeText(text);
  //     alert("Đã sao chép địa chỉ");
  //   } catch (err) {
  //     // Fallback for older browsers
  //     const textArea = document.createElement("textarea");
  //     textArea.value = text;
  //     document.body.appendChild(textArea);
  //     textArea.select();
  //     document.execCommand("copy");
  //     document.body.removeChild(textArea);
  //     alert("Đã sao chép địa chỉ");
  //   }
  // };

  const openInGoogleMaps = (address) => {
    console.log("addess", address);
    const url = address;
    window.open(url, "_blank");
  };

  const loadShipmentDetail = async () => {
    try {
      const result = await shipmentService.getShipmentDetail(shipmentId);
      if (result.success && result.data && result.data.length > 0) {
        const detail = result.data[0];
        setShipmentDetail(detail);

        // Prepare edited shipment with delivery points structure for dispatcher
        const editedData = {
          ...detail,
          deliveryPoints: [],
        };

        // Convert delivery data to delivery points array for editing
        for (let i = 1; i <= 4; i++) {
          const address =
            detail[SHIPMENT_DETAIL_FIELDS[`DELIVERY_ADDRESS_${i}`]];
          if (address) {
            editedData.deliveryPoints.push({
              idKhachHangGiao:
                detail[SHIPMENT_DETAIL_FIELDS[`DELIVERY_CUSTOMER_ID_${i}`]] ||
                "",
              idDiaChiGiaoHang:
                detail[SHIPMENT_DETAIL_FIELDS[`DELIVERY_ADDRESS_ID_${i}`]] ||
                "",
              diaChiGiaoHang: address,
              lienhegiao:
                detail[SHIPMENT_DETAIL_FIELDS[`DELIVERY_CONTACT_${i}`]] || "",
              linkmapgiao:
                detail[SHIPMENT_DETAIL_FIELDS[`DELIVERY_MAP_LINK_${i}`]] || "",
              ngaygiao:
                formatDateFromAPIToInput(
                  detail[SHIPMENT_DETAIL_FIELDS[`DELIVERY_DATE_${i}`]]
                ) || "", // FIX: Convert date format
              giogiao:
                detail[SHIPMENT_DETAIL_FIELDS[`DELIVERY_HOUR_${i}`]] || "",
              phutgiao:
                detail[SHIPMENT_DETAIL_FIELDS[`DELIVERY_MINUTE_${i}`]] || "",
            });
          }
        }

        // Ensure at least one delivery point for editing
        if (editedData.deliveryPoints.length === 0) {
          editedData.deliveryPoints.push({
            idKhachHangGiao: "",
            idDiaChiGiaoHang: "",
            diaChiGiaoHang: "",
            lienhegiao: "",
            linkmapgiao: "",
            ngaygiao: "",
            giogiao: "",
            phutgiao: "",
          });
        }

        // FIX: Convert date formats for input fields
        editedData[SHIPMENT_DETAIL_FIELDS.INPUT_DATE] =
          formatDateFromAPIToInput(detail[SHIPMENT_DETAIL_FIELDS.INPUT_DATE]);
        editedData[SHIPMENT_DETAIL_FIELDS.PICKUP_DATE] =
          formatDateFromAPIToInput(detail[SHIPMENT_DETAIL_FIELDS.PICKUP_DATE]);

        setEditedShipment(editedData);
      } else {
        setError("Không thể tải thông tin chi tiết chuyến xe");
      }
    } catch (err) {
      setError("Có lỗi xảy ra khi tải dữ liệu");
    }
  };

  const handlePickupTimeChange = (timeString) => {
    const { hour, minute } = parseTimeToHourMinute(timeString);
    setEditedShipment((prev) => ({
      ...prev,
      [SHIPMENT_DETAIL_FIELDS.PICKUP_HOUR_1]: hour,
      [SHIPMENT_DETAIL_FIELDS.PICKUP_MINUTE_1]: minute,
    }));
  };

  // Handle delivery time change
  const handleDeliveryTimeChange = (index, timeString) => {
    const { hour, minute } = parseTimeToHourMinute(timeString);
    setEditedShipment((prev) => ({
      ...prev,
      deliveryPoints: prev.deliveryPoints.map((point, i) =>
        i === index ? { ...point, giogiao: hour, phutgiao: minute } : point
      ),
    }));
  };

  const loadMasterData = async () => {
    try {
      const [
        customersResult,
        driversResult,
        trucksResult,
        payloadsResult,
        shippingAddressesResult,
      ] = await Promise.all([
        masterDataService.getCustomers(),
        masterDataService.getDrivers(),
        masterDataService.getTrucks(),
        masterDataService.getPayload(),
        masterDataService.getShippingAddresses(),
      ]);

      if (customersResult.success) setCustomers(customersResult.data || []);
      if (driversResult.success) setDrivers(driversResult.data || []);
      if (trucksResult.success) setTrucks(trucksResult.data || []);
      if (payloadsResult.success) setPayloads(payloadsResult.data || []);
      if (shippingAddressesResult.success)
        setShippingAddresses(shippingAddressesResult.data || []);
    } catch (err) {
      console.error("Error loading master data:", err);
    } finally {
      setLoading(false);
    }
  };

  const getCustomerById = (customerId) => {
    const customer = customers.find(
      (c) => c[CUSTOMER_FIELDS.ID] === customerId
    );
    return customer ? customer[CUSTOMER_FIELDS.NAME] : "";
  };

  const getDriverById = (driverId) => {
    const driver = drivers.find((d) => d[DRIVER_FIELDS.ID] === driverId);
    return driver || null;
  };

  const getTruckById = (truckId) => {
    const truck = trucks.find((t) => t[TRUCK_FIELDS.ID] === truckId);
    return truck || null;
  };

  const getPayloadById = (payloadId) => {
    const payload = payloads.find((p) => p[PAYLOAD_FIELDS.ID] === payloadId);
    return payload ? payload[PAYLOAD_FIELDS.WEIGHT] : "";
  };

  const getNextAvailableStatuses = (currentStatus) => {
    const statusFlow = [
      {
        value: SHIPMENT_STATUS.WAITING_CONFIRMATION,
        text: SHIPMENT_STATUS_TEXT[0],
        color: "bg-blue-500",
      },
      {
        value: SHIPMENT_STATUS.ACCEPTED,
        text: SHIPMENT_STATUS_TEXT[1],
        color: "bg-green-500",
      },
      {
        value: SHIPMENT_STATUS.IN_DELIVERY,
        text: SHIPMENT_STATUS_TEXT[2],
        color: "bg-yellow-500",
      },
      {
        value: SHIPMENT_STATUS.COMPLETED,
        text: SHIPMENT_STATUS_TEXT[5],
        color: "bg-gray-300",
      },
      {
        value: SHIPMENT_STATUS.CANCELLED,
        text: SHIPMENT_STATUS_TEXT[9],
        color: "bg-red-500",
      },
    ];

    const currentIndex = statusFlow.findIndex((s) => s.value === currentStatus);
    if (currentIndex === -1 || currentStatus === SHIPMENT_STATUS.COMPLETED)
      return [];

    // Only allow next status in sequence, plus cancel option (except for completed)
    const nextStatuses = [];
    if (currentIndex < statusFlow.length - 2) {
      // Not completed yet
      nextStatuses.push(statusFlow[currentIndex + 1]);
      if (currentStatus !== SHIPMENT_STATUS.CANCELLED) {
        nextStatuses.push(statusFlow[statusFlow.length - 1]); // Cancel option
      }
    }

    return nextStatuses;
  };

  const handleStatusUpdate = async (newStatus) => {
    if (updating) return;

    // Check if images are required for specific status transitions
    const isTransitionFromAcceptedToInDelivery =
      currentStatus === SHIPMENT_STATUS.ACCEPTED &&
      newStatus === SHIPMENT_STATUS.IN_DELIVERY;

    const isTransitionFromInDeliveryToCompleted =
      currentStatus === SHIPMENT_STATUS.IN_DELIVERY &&
      newStatus === SHIPMENT_STATUS.COMPLETED;

    // Images are required for these specific transitions
    const isImageRequired =
      isTransitionFromAcceptedToInDelivery ||
      isTransitionFromInDeliveryToCompleted;

    // Always require images when transitioning to COMPLETED status
    if (newStatus === SHIPMENT_STATUS.COMPLETED) {
      // Store the pending status change and show upload modal
      setPendingStatusChange(newStatus);
      setShowUploadModal(true);
      return;
    }

    // For other transitions that require images, check if any images exist
    if (isImageRequired && images.length === 0) {
      // Store the pending status change and show upload modal
      setPendingStatusChange(newStatus);
      setShowUploadModal(true);
      return;
    }

    setUpdating(true);
    try {
      // Pass userName from userData to the API call
      const result = await shipmentService.updateStatus(
        shipmentId,
        newStatus,
        userData?.UserName || userData?.userName || ""
      );

      if (result.success) {
        setShipmentDetail((prev) => ({
          ...prev,
          [SHIPMENT_DETAIL_FIELDS.STATUS]: newStatus,
        }));
        alert("Cập nhật trạng thái thành công");
      } else {
        alert(result.message || "Cập nhật trạng thái thất bại");
      }
    } catch (error) {
      alert("Có lỗi xảy ra khi cập nhật trạng thái");
    } finally {
      setUpdating(false);
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing
      setEditedShipment(shipmentDetail);
      setIsEditing(false);
    } else {
      // Start editing
      setIsEditing(true);
      // Expand sections for editing
      if (userData?.type === USER_TYPES.DISPATCHER) {
        setCollapsedSections({
          basicInfo: false,
          vehicleInfo: false,
          notes: false,
        });
      }
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedShipment((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDeliveryPointChange = (index, field, value) => {
    setEditedShipment((prev) => ({
      ...prev,
      deliveryPoints: prev.deliveryPoints.map((point, i) =>
        i === index ? { ...point, [field]: value } : point
      ),
    }));
  };

  const addDeliveryPoint = () => {
    if (editedShipment.deliveryPoints.length < 4) {
      setEditedShipment((prev) => ({
        ...prev,
        deliveryPoints: [
          ...prev.deliveryPoints,
          {
            idKhachHangGiao: "",
            idDiaChiGiaoHang: "",
            diaChiGiaoHang: "",
            lienhegiao: "",
            linkmapgiao: "",
            ngaygiao: "",
            giogiao: "",
            phutgiao: "",
          },
        ],
      }));
    }
  };

  const removeDeliveryPoint = (index) => {
    if (editedShipment.deliveryPoints.length > 1) {
      setEditedShipment((prev) => ({
        ...prev,
        deliveryPoints: prev.deliveryPoints.filter((_, i) => i !== index),
      }));
    }
  };

  const handleCustomerChange = (field, customerId, isPickup = false) => {
    const customer = customers.find(
      (c) => c[CUSTOMER_FIELDS.ID] === parseInt(customerId)
    );

    if (isPickup) {
      handleFieldChange(SHIPMENT_DETAIL_FIELDS.PICKUP_CUSTOMER_ID, customerId);
      if (customer) {
        handleFieldChange(
          SHIPMENT_DETAIL_FIELDS.PICKUP_ADDRESS,
          customer[CUSTOMER_FIELDS.ADDRESS]
        );
        handleFieldChange(
          SHIPMENT_DETAIL_FIELDS.PICKUP_CONTACT,
          customer[CUSTOMER_FIELDS.CONTACT]
        );
      }
    } else {
      // Handle delivery customer change
      const [pointField, pointIndex] = field.split("_");
      const index = parseInt(pointIndex);

      handleDeliveryPointChange(index, "idKhachHangGiao", customerId);
      if (customer) {
        handleDeliveryPointChange(
          index,
          "diaChiGiaoHang",
          customer[CUSTOMER_FIELDS.DELIVERY_ADDRESS] ||
            customer[CUSTOMER_FIELDS.ADDRESS]
        );
        handleDeliveryPointChange(
          index,
          "lienhegiao",
          customer[CUSTOMER_FIELDS.CONTACT]
        );
      }
    }
  };

  const handleSaveEdit = async () => {
    if (saving) return;

    setSaving(true);
    try {
      // Prepare data for API
      const saveData = {
        idChuyenXe: shipmentId,
        ngaynhap: formatDateFromInputToAPI(
          editedShipment[SHIPMENT_DETAIL_FIELDS.INPUT_DATE]
        ), // FIX: Convert format
        ngaynhan: formatDateFromInputToAPI(
          editedShipment[SHIPMENT_DETAIL_FIELDS.PICKUP_DATE]
        ), // FIX: Convert format
        loaivantai: editedShipment[SHIPMENT_DETAIL_FIELDS.TRANSPORT_TYPE],
        idTrongTai: editedShipment[SHIPMENT_DETAIL_FIELDS.PAYLOAD_ID],
        idXe: editedShipment[SHIPMENT_DETAIL_FIELDS.TRUCK_ID],
        idLaiXe: editedShipment[SHIPMENT_DETAIL_FIELDS.DRIVER_ID],

        // Required field - keep main customer
        idKhachHang: editedShipment[SHIPMENT_DETAIL_FIELDS.PICKUP_CUSTOMER_ID],
        idKhachHangNhan:
          editedShipment[SHIPMENT_DETAIL_FIELDS.PICKUP_CUSTOMER_ID],

        idDiaChiLayHang:
          editedShipment[SHIPMENT_DETAIL_FIELDS.PICKUP_ADDRESS_ID],
        diaChiLayHang: editedShipment[SHIPMENT_DETAIL_FIELDS.PICKUP_ADDRESS],
        lienhenhan: editedShipment[SHIPMENT_DETAIL_FIELDS.PICKUP_CONTACT],
        linkmapnhan: editedShipment[SHIPMENT_DETAIL_FIELDS.PICKUP_MAP_LINK],
        gionhan1: editedShipment[SHIPMENT_DETAIL_FIELDS.PICKUP_HOUR_1],
        phutnhan1: editedShipment[SHIPMENT_DETAIL_FIELDS.PICKUP_MINUTE_1],
        giatri: editedShipment[SHIPMENT_DETAIL_FIELDS.VALUE],
        ghichu: editedShipment[SHIPMENT_DETAIL_FIELDS.NOTES],
        trangthai: editedShipment[SHIPMENT_DETAIL_FIELDS.STATUS],
        userName: userData.UserName || userData.userName || "",
      };

      // Add delivery points
      editedShipment.deliveryPoints.forEach((point, index) => {
        const pointNum = index + 1;
        saveData[`ngaygiao${pointNum}`] = formatDateFromInputToAPI(
          point.ngaygiao
        ); // FIX: Convert format
        saveData[`idKhachHangGiao${pointNum}`] = point.idKhachHangGiao;
        saveData[`idDiaChiGiaoHang${pointNum}`] = point.idDiaChiGiaoHang;
        saveData[`diaChiGiaoHang${pointNum}`] = point.diaChiGiaoHang;
        saveData[`lienhegiao${pointNum}`] = point.lienhegiao;
        saveData[`linkmapgiao${pointNum}`] = point.linkmapgiao;
        saveData[`giogiao${pointNum}`] = point.giogiao;
        saveData[`phutgiao${pointNum}`] = point.phutgiao;
      });

      const result = await shipmentService.saveShipment(saveData);
      if (result.success) {
        setIsEditing(false);
        alert("Cập nhật thông tin chuyến xe thành công");
        // Reload detail to get fresh data
        loadShipmentDetail();
      } else {
        alert(result.message || "Cập nhật thông tin thất bại");
      }
    } catch (error) {
      console.error("Error saving shipment:", error);
      alert("Có lỗi xảy ra khi cập nhật thông tin");
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (hour, minute) => {
    if (!hour && !minute) return "";
    return `${String(hour || 0).padStart(2, "0")}:${String(
      minute || 0
    ).padStart(2, "0")}`;
  };

  const getDeliveryAddresses = () => {
    if (!shipmentDetail) return [];

    const addresses = [];
    for (let i = 1; i <= 4; i++) {
      const address =
        shipmentDetail[SHIPMENT_DETAIL_FIELDS[`DELIVERY_ADDRESS_${i}`]];
      const contact =
        shipmentDetail[SHIPMENT_DETAIL_FIELDS[`DELIVERY_CONTACT_${i}`]];
      const date = shipmentDetail[SHIPMENT_DETAIL_FIELDS[`DELIVERY_DATE_${i}`]];
      const hour = shipmentDetail[SHIPMENT_DETAIL_FIELDS[`DELIVERY_HOUR_${i}`]];
      const minute =
        shipmentDetail[SHIPMENT_DETAIL_FIELDS[`DELIVERY_MINUTE_${i}`]];
      const linkmap =
        shipmentDetail[SHIPMENT_DETAIL_FIELDS[`DELIVERY_MAP_LINK_${i}`]];

      if (address) {
        addresses.push({
          address,
          contact,
          date,
          time: formatTime(hour, minute),
          index: i,
          linkmap,
        });
      }
    }
    return addresses;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Đang tải dữ liệu...</p>
        </div>
      </div>
    );
  }

  if (error || !shipmentDetail) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            {error || "Không tìm thấy thông tin chuyến xe"}
          </p>
          <button
            onClick={onBack}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Quay lại
          </button>
        </div>
      </div>
    );
  }

  const currentStatus = shipmentDetail[SHIPMENT_DETAIL_FIELDS.STATUS];
  const driver = getDriverById(
    shipmentDetail[SHIPMENT_DETAIL_FIELDS.DRIVER_ID]
  );
  const truck = getTruckById(shipmentDetail[SHIPMENT_DETAIL_FIELDS.TRUCK_ID]);
  const payload = getPayloadById(
    shipmentDetail[SHIPMENT_DETAIL_FIELDS.PAYLOAD_ID]
  );
  const deliveryAddresses = getDeliveryAddresses();
  const nextStatuses = getNextAvailableStatuses(currentStatus);
  const isStatusEditable =
    currentStatus !== SHIPMENT_STATUS.COMPLETED &&
    userData?.type === USER_TYPES.DRIVER;
  const canCancelShipment =
    userData?.type === USER_TYPES.DISPATCHER &&
    currentStatus !== SHIPMENT_STATUS.COMPLETED &&
    currentStatus !== SHIPMENT_STATUS.CANCELLED;
  const canEditShipment =
    userData?.type === USER_TYPES.DISPATCHER &&
    currentStatus !== SHIPMENT_STATUS.COMPLETED;

  return (
    <div
      className="p-4 bg-gray-50 min-h-screen pb-20"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      ref={containerRef}
    >
      <div className="max-w-4xl mx-auto">
        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold mb-4 text-center">Upload ảnh</h3>
              <p className="text-gray-700 mb-4 text-center">
                Bắt buộc phải upload ảnh khi chuyển trạng thái này.
              </p>

              <div className="mb-4">
                <ImageUpload
                  shipmentId={shipmentId}
                  userData={userData}
                  onUploadComplete={handleModalImageUploadComplete}
                  onUploadError={handleImageUploadError}
                  maxFiles={10}
                  imageType="shipment"
                  trangthai={pendingStatusChange}
                />
              </div>

              <div className="flex justify-end gap-3 mt-4">
               
                <button
                  onClick={completeStatusUpdateAfterUpload}
                  disabled={uploadedImagesInModal.length === 0}
                  className={`px-4 py-2 rounded-lg ${
                    uploadedImagesInModal.length > 0
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  {uploadedImagesInModal.length > 0
                    ? `Hoàn thành và chuyển trạng thái (${uploadedImagesInModal.length} ảnh)`
                    : "Vui lòng upload ít nhất 1 ảnh"}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Header - Hiển thị tiêu đề và trạng thái */}
        <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center">
      <button onClick={onBack} className="mr-4 p-2 hover:bg-gray-100 rounded-lg">
        <ChevronLeft className="w-6 h-6" />
      </button>
      <h2 className="text-xl font-bold">Chi tiết chuyến xe #{shipmentId}</h2>
    </div>
    
    {/* Copy buttons và Image count badge */}
    <div className="flex items-center gap-2">
      {/* Copy Driver Info Button */}
      {driver && truck && (
        <button
          onClick={handleCopyDriverInfo}
          disabled={copyingType === 'driver'}
          className="flex items-center gap-1 px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
          title="Sao chép thông tin lái xe"
        >
          {copyingType === 'driver' ? (
            <div className="animate-spin h-4 w-4 border-2 border-green-600 rounded-full border-t-transparent"></div>
          ) : copySuccess && copyingType === null ? (
            <Check className="w-4 h-4" />
          ) : (
            <Share2 className="w-4 h-4" />
          )}
          <span className="hidden sm:inline">
            {copyingType === 'driver' ? 'Đang sao chép...' : 'Lái xe'}
          </span>
        </button>
      )}

      {/* Copy Complete Info Button */}
      {driver && truck && (
        <button
          onClick={handleCopyCompleteInfo}
          disabled={copyingType === 'complete'}
          className="flex items-center gap-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
          title="Sao chép thông tin đầy đủ"
        >
          {copyingType === 'complete' ? (
            <div className="animate-spin h-4 w-4 border-2 border-blue-600 rounded-full border-t-transparent"></div>
          ) : copySuccess && copyingType === null ? (
            <Check className="w-4 h-4" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
          <span className="hidden sm:inline">
            {copyingType === 'complete' ? 'Đang sao chép...' : 'Đầy đủ'}
          </span>
        </button>
      )}
      
      {/* Image count badge */}
      {images.length > 0 && (
        <button onClick={scrollToImagesSection} className="flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200 transition-colors">
          <Camera className="w-4 h-4" />
          <span>{images.length} ảnh</span>
        </button>
      )}
    </div>
  </div>

  {/* Copy Success Toast */}
  {copySuccess && (
    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
      <div className="flex items-center text-green-700">
        <Check className="w-5 h-5 mr-2" />
        <span className="text-sm font-medium">
          Đã sao chép thông tin thành công! Có thể dán vào tin nhắn gửi khách hàng.
        </span>
      </div>
    </div>
  )}

          {/* Status Badge */}
          <div className="mb-4 flex items-center justify-between">
            <div
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                SHIPMENT_STATUS_COLORS[currentStatus]
              } ${
                currentStatus === SHIPMENT_STATUS.COMPLETED
                  ? "text-gray-900 font-bold"
                  : "text-white"
              }`}
            >
              {SHIPMENT_STATUS_TEXT[currentStatus]}
            </div>

            {canCancelShipment && !isEditing && (
              <button
                onClick={() => handleStatusUpdate(SHIPMENT_STATUS.CANCELLED)}
                disabled={updating}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {updating ? "Đang xử lý..." : "Xoá chuyến"}
              </button>
            )}
          </div>
        </div>

        {/* Status Update Section - Dành cho tài xế */}
        {isStatusEditable && nextStatuses.length > 0 && !isEditing && (
          <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
            <h3 className="text-lg font-semibold mb-4">Cập nhật trạng thái</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {nextStatuses.map((statusOption) => (
                <button
                  key={statusOption.value}
                  onClick={() => handleStatusUpdate(statusOption.value)}
                  disabled={updating}
                  className={`${statusOption.color} text-white py-3 px-4 rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed hover:opacity-90 transition-opacity font-medium`}
                >
                  {updating
                    ? "Đang cập nhật..."
                    : `Chuyển sang: ${statusOption.text}`}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 1. Thông tin cơ bản */}
        <div className="bg-white rounded-lg shadow-lg mb-4">
          <button
            onClick={() => !isEditing && toggleSection("basicInfo")}
            className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
            disabled={isEditing}
          >
            <h3 className="text-lg font-semibold">Thông tin cơ bản</h3>
            {!isEditing &&
              (collapsedSections.basicInfo ? (
                <ChevronDown className="w-5 h-5" />
              ) : (
                <ChevronUp className="w-5 h-5" />
              ))}
          </button>
          {(!collapsedSections.basicInfo || isEditing) && (
            <div className="p-4 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Loại vận tải
                  </label>
                  {isEditing ? (
                    <select
                      value={
                        editedShipment[SHIPMENT_DETAIL_FIELDS.TRANSPORT_TYPE] ||
                        ""
                      }
                      onChange={(e) =>
                        handleFieldChange(
                          SHIPMENT_DETAIL_FIELDS.TRANSPORT_TYPE,
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value={TRANSPORT_TYPES.SINGLE_POINT}>
                        Giao một điểm
                      </option>
                      <option value={TRANSPORT_TYPES.MULTIPLE_POINTS}>
                        Giao nhiều điểm
                      </option>
                      <option value={TRANSPORT_TYPES.BY_DAY}>Theo ngày</option>
                    </select>
                  ) : (
                    <p className="font-medium">
                      {shipmentDetail[SHIPMENT_DETAIL_FIELDS.TRANSPORT_TYPE] ===
                      TRANSPORT_TYPES.SINGLE_POINT
                        ? "Giao một điểm"
                        : shipmentDetail[
                            SHIPMENT_DETAIL_FIELDS.TRANSPORT_TYPE
                          ] === TRANSPORT_TYPES.MULTIPLE_POINTS
                        ? "Giao nhiều điểm"
                        : "Theo ngày"}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <CalendarIcon className="w-4 h-4 inline mr-1" />
                    Ngày nhập
                  </label>
                  {isEditing ? (
                    <input
                      type="date"
                      value={
                        editedShipment[SHIPMENT_DETAIL_FIELDS.INPUT_DATE] || ""
                      }
                      onChange={(e) =>
                        handleFieldChange(
                          SHIPMENT_DETAIL_FIELDS.INPUT_DATE,
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="DD-MM-YYYY"
                    />
                  ) : (
                    <p className="font-medium">
                      {shipmentDetail[SHIPMENT_DETAIL_FIELDS.INPUT_DATE]}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Khách hàng <span className="text-red-500">*</span>
                  </label>
                  {isEditing ? (
                    <select
                      value={
                        editedShipment[SHIPMENT_DETAIL_FIELDS.CUSTOMER_ID] || ""
                      }
                      onChange={(e) =>
                        handleFieldChange(
                          SHIPMENT_DETAIL_FIELDS.CUSTOMER_ID,
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Chọn khách hàng</option>
                      {customers.map((customer) => (
                        <option
                          key={customer[CUSTOMER_FIELDS.ID]}
                          value={customer[CUSTOMER_FIELDS.ID]}
                        >
                          {customer[CUSTOMER_FIELDS.CODE]} -{" "}
                          {customer[CUSTOMER_FIELDS.NAME]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="font-medium">
                      {getCustomerById(
                        shipmentDetail[SHIPMENT_DETAIL_FIELDS.CUSTOMER_ID]
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 2. Thông tin xe và lái xe */}
        <div className="bg-white rounded-lg shadow-lg mb-4">
          <button
            onClick={() => !isEditing && toggleSection("vehicleInfo")}
            className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
            disabled={isEditing}
          >
            <h3 className="text-lg font-semibold">
              <Truck className="w-5 h-5 inline mr-2" />
              Thông tin xe và lái xe
            </h3>
            {!isEditing &&
              (collapsedSections.vehicleInfo ? (
                <ChevronDown className="w-5 h-5" />
              ) : (
                <ChevronUp className="w-5 h-5" />
              ))}
          </button>
          {(!collapsedSections.vehicleInfo || isEditing) && (
            <div className="p-4 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tải trọng <span className="text-red-500">*</span>
                  </label>
                  {isEditing ? (
                    <select
                      value={
                        editedShipment[SHIPMENT_DETAIL_FIELDS.PAYLOAD_ID] || ""
                      }
                      onChange={(e) =>
                        handleFieldChange(
                          SHIPMENT_DETAIL_FIELDS.PAYLOAD_ID,
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Chọn tải trọng</option>
                      {payloads.map((payload) => (
                        <option
                          key={payload[PAYLOAD_FIELDS.ID]}
                          value={payload[PAYLOAD_FIELDS.ID]}
                        >
                          {payload[PAYLOAD_FIELDS.WEIGHT]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="font-medium">{payload || "N/A"}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Xe <span className="text-red-500">*</span>
                  </label>
                  {isEditing ? (
                    <select
                      value={
                        editedShipment[SHIPMENT_DETAIL_FIELDS.TRUCK_ID] || ""
                      }
                      onChange={(e) =>
                        handleFieldChange(
                          SHIPMENT_DETAIL_FIELDS.TRUCK_ID,
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Chọn xe</option>
                      {trucks.map((truck) => (
                        <option
                          key={truck[TRUCK_FIELDS.ID]}
                          value={truck[TRUCK_FIELDS.ID]}
                        >
                          {truck[TRUCK_FIELDS.LICENSE_PLATE]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="font-medium">
                      {truck ? truck[TRUCK_FIELDS.LICENSE_PLATE] : "N/A"}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-1" />
                    Lái xe <span className="text-red-500">*</span>
                  </label>
                  {isEditing ? (
                    <select
                      value={
                        editedShipment[SHIPMENT_DETAIL_FIELDS.DRIVER_ID] || ""
                      }
                      onChange={(e) =>
                        handleFieldChange(
                          SHIPMENT_DETAIL_FIELDS.DRIVER_ID,
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Chọn lái xe</option>
                      {drivers.map((driver) => (
                        <option
                          key={driver[DRIVER_FIELDS.ID]}
                          value={driver[DRIVER_FIELDS.ID]}
                        >
                          {driver[DRIVER_FIELDS.NAME]} -{" "}
                          {driver[DRIVER_FIELDS.PHONE]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="font-medium">
                      {driver ? driver[DRIVER_FIELDS.NAME] : "N/A"}
                    </p>
                  )}
                </div>
                {!isEditing && driver && (
                  <>
                    <div>
                      <label className="text-sm text-gray-600">
                        Điện thoại
                      </label>
                      <p className="font-medium flex items-center">
                        <Phone className="w-4 h-4 mr-1" />
                        {driver[DRIVER_FIELDS.PHONE] || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">CCCD</label>
                      <p className="font-medium">
                        {driver[DRIVER_FIELDS.ID_CARD] || "N/A"}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 3. Thông tin lấy hàng */}
        <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
          <h3 className="text-lg font-semibold mb-4 text-blue-600">
            <MapPin className="w-5 h-5 inline mr-2" />
            Thông tin lấy hàng
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nhận <span className="text-red-500">*</span>
                </label>
                {isEditing ? (
                  <select
                    value={
                      editedShipment[
                        SHIPMENT_DETAIL_FIELDS.PICKUP_CUSTOMER_ID
                      ] || ""
                    }
                    onChange={(e) =>
                      handleCustomerChange("pickup", e.target.value, true)
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Chọn điểm nhận</option>
                    {customers.map((customer) => (
                      <option
                        key={customer[CUSTOMER_FIELDS.ID]}
                        value={customer[CUSTOMER_FIELDS.ID]}
                      >
                        {customer[CUSTOMER_FIELDS.CODE]} -{" "}
                        {customer[CUSTOMER_FIELDS.NAME]}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="font-medium text-lg">
                    {getCustomerById(
                      shipmentDetail[SHIPMENT_DETAIL_FIELDS.PICKUP_CUSTOMER_ID]
                    )}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Địa chỉ nhận <span className="text-red-500">*</span>
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={
                    editedShipment[SHIPMENT_DETAIL_FIELDS.PICKUP_ADDRESS] || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(
                      SHIPMENT_DETAIL_FIELDS.PICKUP_ADDRESS,
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Nhập địa chỉ lấy hàng"
                />
              ) : (
                <div className="flex items-start gap-2">
                  <p className="font-medium flex-1">
                    {shipmentDetail[SHIPMENT_DETAIL_FIELDS.PICKUP_ADDRESS]}
                  </p>
                  <div className="flex gap-1">
                    <button
                      onClick={() =>
                        copyToClipboard(
                          shipmentDetail[SHIPMENT_DETAIL_FIELDS.PICKUP_ADDRESS]
                        )
                      }
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      title="Sao chép địa chỉ"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() =>
                        openInGoogleMaps(
                          shipmentDetail[SHIPMENT_DETAIL_FIELDS.PICKUP_MAP_LINK]
                        )
                      }
                      className="p-2 text-green-600 hover:bg-green-50 rounded"
                      title="Mở bản đồ"
                    >
                      <Navigation className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Liên hệ
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={
                      editedShipment[SHIPMENT_DETAIL_FIELDS.PICKUP_CONTACT] ||
                      ""
                    }
                    onChange={(e) =>
                      handleFieldChange(
                        SHIPMENT_DETAIL_FIELDS.PICKUP_CONTACT,
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Tên người liên hệ & số điện thoại"
                  />
                ) : (
                  renderContactWithPhones(
                    shipmentDetail[SHIPMENT_DETAIL_FIELDS.PICKUP_CONTACT]
                  )
                )}
              </div>
              {isEditing ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Navigation className="w-4 h-4 inline mr-1" />
                    Link bản đồ nhận hàng
                  </label>
                  <input
                    type="url"
                    value={
                      editedShipment[SHIPMENT_DETAIL_FIELDS.PICKUP_MAP_LINK] ||
                      ""
                    }
                    onChange={(e) =>
                      handleFieldChange(
                        SHIPMENT_DETAIL_FIELDS.PICKUP_MAP_LINK,
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="https://maps.google.com/..."
                  />
                </div>
              ) : null}
              {!isEditing &&
                shipmentDetail[SHIPMENT_DETAIL_FIELDS.PICKUP_MAP_LINK] && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Navigation className="w-4 h-4 inline mr-1" />
                      Bản đồ nhận hàng
                    </label>
                    <a
                      href={
                        shipmentDetail[SHIPMENT_DETAIL_FIELDS.PICKUP_MAP_LINK]
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                    >
                      Mở bản đồ <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <CalendarIcon className="w-4 h-4 inline mr-1" />
                  Ngày nhận hàng
                </label>
                {isEditing ? (
                  <input
                    type="date"
                    value={
                      editedShipment[SHIPMENT_DETAIL_FIELDS.PICKUP_DATE] || ""
                    }
                    onChange={(e) =>
                      handleFieldChange(
                        SHIPMENT_DETAIL_FIELDS.PICKUP_DATE,
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="DD-MM-YYYY"
                  />
                ) : (
                  <p className="font-medium">
                    {shipmentDetail[SHIPMENT_DETAIL_FIELDS.PICKUP_DATE]}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Thời gian lấy hàng
                </label>
                {isEditing ? (
                  <input
                    type="time"
                    value={formatTimeFromHourMinute(
                      editedShipment[SHIPMENT_DETAIL_FIELDS.PICKUP_HOUR_1],
                      editedShipment[SHIPMENT_DETAIL_FIELDS.PICKUP_MINUTE_1]
                    )}
                    onChange={(e) => handlePickupTimeChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                ) : (
                  <p className="font-medium text-lg text-orange-600">
                    {formatTime(
                      shipmentDetail[SHIPMENT_DETAIL_FIELDS.PICKUP_HOUR_1],
                      shipmentDetail[SHIPMENT_DETAIL_FIELDS.PICKUP_MINUTE_1]
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Delivery Information - Always visible and editable for dispatcher */}
        <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-green-600">
              <Truck className="w-5 h-5 inline mr-2" />
              Thông tin giao hàng
            </h3>
            {isEditing &&
              editedShipment.deliveryPoints &&
              editedShipment.deliveryPoints.length < 4 && (
                <button
                  onClick={addDeliveryPoint}
                  className="bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 flex items-center gap-1 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Thêm điểm giao
                </button>
              )}
          </div>

          <div className="space-y-6">
            {isEditing
              ? // Edit mode - show editable delivery points
                editedShipment.deliveryPoints &&
                editedShipment.deliveryPoints.map((point, index) => (
                  <div key={index} className="border-l-4 border-green-500 pl-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-green-600">
                        Điểm giao {index + 1}
                      </h4>
                      {editedShipment.deliveryPoints.length > 1 && (
                        <button
                          onClick={() => removeDeliveryPoint(index)}
                          className="text-red-600 hover:text-red-800 p-1"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-600">
                            Khách hàng
                          </label>
                          <select
                            value={point.idKhachHangGiao}
                            onChange={(e) =>
                              handleCustomerChange(
                                `delivery_${index}`,
                                e.target.value
                              )
                            }
                            className="w-full px-3 py-2 border rounded-lg"
                          >
                            <option value="">Chọn khách hàng</option>
                            {customers.map((customer) => (
                              <option
                                key={customer[CUSTOMER_FIELDS.ID]}
                                value={customer[CUSTOMER_FIELDS.ID]}
                              >
                                {customer[CUSTOMER_FIELDS.CODE]} -{" "}
                                {customer[CUSTOMER_FIELDS.NAME]}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="text-sm text-gray-600">
                          <MapPin className="w-4 h-4 inline mr-1" />
                          Địa chỉ giao hàng
                        </label>
                        <input
                          type="text"
                          value={point.diaChiGiaoHang}
                          onChange={(e) =>
                            handleDeliveryPointChange(
                              index,
                              "diaChiGiaoHang",
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-2 border rounded-lg"
                          placeholder="Nhập địa chỉ giao hàng"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-sm text-gray-600">
                            <Phone className="w-4 h-4 inline mr-1" />
                            Liên hệ
                          </label>
                          <input
                            type="text"
                            value={point.lienhegiao}
                            onChange={(e) =>
                              handleDeliveryPointChange(
                                index,
                                "lienhegiao",
                                e.target.value
                              )
                            }
                            className="w-full px-3 py-2 border rounded-lg"
                            placeholder="Tên & SĐT người nhận"
                          />
                        </div>
                        {isEditing ? (
                          <div>
                            <label className="text-sm text-gray-600">
                              <Navigation className="w-4 h-4 inline mr-1" />
                              Link bản đồ giao hàng
                            </label>
                            <input
                              type="url"
                              value={point.linkmapgiao}
                              onChange={(e) =>
                                handleDeliveryPointChange(
                                  index,
                                  "linkmapgiao",
                                  e.target.value
                                )
                              }
                              className="w-full px-3 py-2 border rounded-lg"
                              placeholder="https://maps.google.com/..."
                            />
                          </div>
                        ) : null}

                        <div>
                          <label className="text-sm text-gray-600">
                            <CalendarIcon className="w-4 h-4 inline mr-1" />
                            Ngày giao
                          </label>
                          <input
                            type="date"
                            value={point.ngaygiao}
                            onChange={(e) =>
                              handleDeliveryPointChange(
                                index,
                                "ngaygiao",
                                e.target.value
                              )
                            }
                            className="w-full px-3 py-2 border rounded-lg"
                            placeholder="DD-MM-YYYY"
                          />
                        </div>
                        <div>
                          <label className="text-sm text-gray-600">
                            <Clock className="w-4 h-4 inline mr-1" />
                            Thời gian giao
                          </label>
                          <input
                            type="time"
                            value={formatTimeFromHourMinute(
                              point.giogiao,
                              point.phutgiao
                            )}
                            onChange={(e) =>
                              handleDeliveryTimeChange(index, e.target.value)
                            }
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              : // View mode - show existing delivery addresses
                deliveryAddresses.map((delivery, index) => (
                  <div key={index} className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-medium text-green-600 mb-2">
                      Điểm giao {delivery.index}
                    </h4>
                    <div className="space-y-2">
                      <div>
                        <label className="text-sm text-gray-600">
                          Khách hàng
                        </label>
                        <p className="font-medium">
                          {getCustomerById(
                            shipmentDetail[
                              SHIPMENT_DETAIL_FIELDS[
                                `DELIVERY_CUSTOMER_ID_${delivery.index}`
                              ]
                            ]
                          )}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">
                          Địa chỉ giao hàng
                        </label>
                        <div className="flex items-start gap-2">
                          <p className="font-medium flex-1">
                            {delivery.address}
                          </p>
                          <div className="flex gap-1">
                            <button
                              onClick={() => copyToClipboard(delivery.address)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                              title="Sao chép địa chỉ"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openInGoogleMaps(delivery.linkmap)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded"
                              title="Mở bản đồ"
                            >
                              <Navigation className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600 flex items-center">
                          <Phone className="w-4 h-4 mr-1" />
                          Liên hệ
                        </label>
                        {renderContactWithPhones(delivery.contact)}
                        {shipmentDetail[
                          SHIPMENT_DETAIL_FIELDS[
                            `DELIVERY_MAP_LINK_${delivery.index}`
                          ]
                        ] && (
                          <div>
                            <label className="text-sm text-gray-600 flex items-center">
                              <Navigation className="w-4 h-4 mr-1" />
                              Bản đồ giao hàng
                            </label>
                            <a
                              href={
                                shipmentDetail[
                                  SHIPMENT_DETAIL_FIELDS[
                                    `DELIVERY_MAP_LINK_${delivery.index}`
                                  ]
                                ]
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                            >
                              Mở bản đồ <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-600">
                            Ngày giao
                          </label>
                          <p className="font-medium">{delivery.date}</p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600 flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            Thời gian giao hàng
                          </label>
                          <p className="font-medium text-lg text-blue-600">
                            {delivery.time}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
          </div>
        </div>

        {/* Image Upload Section - High priority for drivers */}
        {userData?.type === USER_TYPES.DRIVER && (
          <div
            ref={imageUploadSectionRef}
            className="bg-white rounded-lg shadow-lg mb-4 image-upload-section"
          >
            <button
              onClick={() => toggleSection("imageUpload")}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
            >
              <h3 className="text-lg font-semibold flex items-center text-blue-600">
                <Camera className="w-5 h-5 mr-2" />
                Upload ảnh đơn hàng
              </h3>
              {collapsedSections.imageUpload ? (
                <ChevronDown className="w-5 h-5" />
              ) : (
                <ChevronUp className="w-5 h-5" />
              )}
            </button>

            {!collapsedSections.imageUpload && (
              <div className="p-4 pt-0">
                <div className="flex items-center justify-end mb-4">
                  {!showImageUpload && (
                    <button
                      onClick={() => setShowImageUpload(true)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      <Camera className="w-4 h-4" />
                      Bắt đầu upload
                    </button>
                  )}
                </div>

                {showImageUpload ? (
                  <div className="space-y-4">
                    <ImageUpload
                      shipmentId={shipmentId}
                      userData={userData}
                      onUploadComplete={handleImageUploadComplete}
                      onUploadError={handleImageUploadError}
                      maxFiles={10}
                      imageType="shipment"
                      disabled={currentStatus === SHIPMENT_STATUS.COMPLETED}
                      trangthai={currentStatus}
                    />

                    <div className="flex justify-end">
                      <button
                        onClick={() => setShowImageUpload(false)}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        Ẩn upload ảnh
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-2">
                      Sẵn sàng upload ảnh đơn hàng
                    </p>
                    <p className="text-sm text-gray-500">
                      Chụp ảnh biên bản đã ký và hàng hóa tại điểm giao để hoàn
                      thành chuyến xe
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Shipment Images Section */}
        {images && images.length > 0 && (
          <div
            ref={imagesSectionRef}
            className="bg-white rounded-lg shadow-lg mb-4"
          >
            <button
              onClick={() => toggleSection("images")}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
            >
              <h3 className="text-lg font-semibold flex items-center text-blue-600">
                <Camera className="w-5 h-5 mr-2" />
                Hình ảnh đơn hàng ({images.length})
              </h3>
              {collapsedSections.images ? (
                <ChevronDown className="w-5 h-5" />
              ) : (
                <ChevronUp className="w-5 h-5" />
              )}
            </button>
            {!collapsedSections.images && (
              <div className="p-4 pt-0">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {images.map((image, index) => (
                    <div
                      key={index}
                      className="relative group border border-gray-200 rounded-lg overflow-hidden"
                    >
                      <a
                        href={`https://td.giangdc.company${image.duongdan.substring(
                          1
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={`https://td.giangdc.company${image.duongdan.substring(
                            1
                          )}`}
                          alt={`Ảnh biên bản ${index + 1}`}
                          className="w-full h-32 object-cover hover:opacity-90 transition-all"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 flex items-center justify-center transition-all">
                          <ExternalLink className="text-white opacity-0 group-hover:opacity-100 w-6 h-6" />
                        </div>
                      </a>

                      {/* Delete button */}
                      {userData?.type === USER_TYPES.DRIVER && (
                        <button
                          onClick={() =>
                            handleDeleteImage(image.id, image.duongdan)
                          }
                          disabled={deletingImageId === image.id}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 shadow-lg"
                          title="Xóa ảnh"
                        >
                          {deletingImageId === image.id ? (
                            <div className="animate-spin h-3 w-3 border-2 border-white rounded-full border-t-transparent"></div>
                          ) : (
                            <Trash2 className="w-3 h-3" />
                          )}
                        </button>
                      )}

                      {/* Image info */}
                      <div className="p-2 bg-gray-50 text-xs text-gray-600">
                        <p className="truncate">
                          {image.tenfile || `Ảnh ${index + 1}`}
                        </p>
                        <p className="text-gray-500">
                          {new Date(image.ngaytao).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Collapsible Notes - Editable for Dispatcher */}
        <div className="bg-white rounded-lg shadow-lg mb-4">
          <button
            onClick={() => !isEditing && toggleSection("notes")}
            className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
            disabled={isEditing}
          >
            <h3 className="text-lg font-semibold">Ghi chú</h3>
            {!isEditing &&
              (collapsedSections.notes ? (
                <ChevronDown className="w-5 h-5" />
              ) : (
                <ChevronUp className="w-5 h-5" />
              ))}
          </button>
          {(!collapsedSections.notes || isEditing) && (
            <div className="p-4 pt-0">
              {isEditing ? (
                <textarea
                  value={editedShipment[SHIPMENT_DETAIL_FIELDS.NOTES] || ""}
                  onChange={(e) =>
                    handleFieldChange(
                      SHIPMENT_DETAIL_FIELDS.NOTES,
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="3"
                  placeholder="Nhập ghi chú cho chuyến xe..."
                />
              ) : (
                <p className="text-gray-700">
                  {shipmentDetail[SHIPMENT_DETAIL_FIELDS.NOTES] ||
                    "Không có ghi chú"}
                </p>
              )}
            </div>
          )}
          {canEditShipment && (
            <div className="bg-white rounded-lg shadow-lg p-4">
              <div className="flex justify-end gap-3">
                {isEditing ? (
                  <>
                    <button
                      onClick={handleEditToggle}
                      className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Hủy
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      disabled={saving}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? "Đang lưu..." : "Lưu"}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleEditToggle}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Edit2 className="w-4 h-4" />
                    Sửa
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShipmentDetail;
